import { fromUnixTime } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { sprintf } from 'sprintf-js';

import { OrgStatBadge } from 'components/OrgStatBadge/OrgStatBadge';
import { BodyMicro } from 'components/Text/BodyMicro';
import { useUser } from 'contexts/UserEnvironmentContext';
import type { SearchAlgoliaOrgHit } from 'modules/search/algolia/types/SearchAlgoliaOrgHit';
import { SearchHitImage } from 'modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitOwnerOrgBanner } from 'modules/search/components/Hit/OrgOwnerBanner/SearchHitOwnerOrgBanner';
import { SearchHitSaveButtonContainer } from 'modules/search/components/Hit/SaveButton/SearchHitSaveButtonContainer';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitHoverEffect,
  SearchHitImageHolder,
  SearchHitSaveButtonHolder,
} from 'modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from 'modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { getCityStateString } from 'utils/address/getCityStateString';
import { AREAS_OF_FOCUS } from 'utils/constants/general/areasOfFocus';
import { distanceInWordsToNow } from 'utils/date';
import { shuffleArray } from 'utils/functional/array';

import { SearchOrgHitInfo } from './SearchOrgHitInfo';

type Props = {
  hit: SearchAlgoliaOrgHit;
  href: string;
  trackClick?: () => void;
  showLabel?: boolean;
  hideCta?: boolean;
  hideOwnerBanner?: boolean;
  highlighted?: boolean;
};

export function SearchOrgHit({
  hit,
  href,
  trackClick,
  showLabel,
  hideCta,
  hideOwnerBanner,
  highlighted,
}: Props) {
  const { user } = useUser();
  const {
    objectID,
    type,
    name,
    orgType,
    published,
    city,
    state,
    country,
    logo,
    hiring,
    seekingVolunteers,
    hasUpcomingEvents,
    areasOfFocus,
  } = hit;

  const location = useMemo(
    () =>
      getCityStateString({
        city,
        stateCode: state,
        country,
      }),
    [city, country, state],
  );

  const pillItems: string[] = useMemo(() => {
    if (type === 'ORG' && areasOfFocus) {
      let trimmedAreasOfFocus = areasOfFocus;
      if (areasOfFocus.length > 3) {
        trimmedAreasOfFocus = shuffleArray(areasOfFocus).slice(0, 3);
      }

      return trimmedAreasOfFocus.map(
        (areaOfFocus) => AREAS_OF_FOCUS[areaOfFocus],
      );
    }
    return [];
  }, [areasOfFocus, type]);

  const orgStat = useMemo(() => {
    if (hiring) {
      return (
        <OrgStatBadge iconName="star-check" text={getText('Actively Hiring')} />
      );
    }
    if (hasUpcomingEvents) {
      return (
        <OrgStatBadge iconName="calendar" text={getText('Upcoming Events')} />
      );
    }
    if (seekingVolunteers) {
      return (
        <OrgStatBadge iconName="group" text={getText('Seeking Volunteers')} />
      );
    }
    return null;
  }, [hasUpcomingEvents, hiring, seekingVolunteers]);

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (published) {
      stats.push(
        <BodyMicro lineHeight={1}>
          {sprintf(getText('Posted %(postedAt)s'), {
            postedAt: distanceInWordsToNow(fromUnixTime(published)),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [published]);

  const showSaveButton = !hideCta;

  const ownerOrg = user ? user.orgs.find((o) => o.id === objectID) : null;

  return (
    <SearchHitHolder data-qa-id="search-result" data-hit-id={objectID}>
      {!hideOwnerBanner && ownerOrg && (
        <SearchHitOwnerOrgBanner org={ownerOrg} hit={hit} />
      )}

      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          roundedTop={hideOwnerBanner || !ownerOrg}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitHoverEffect />

          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={undefined}
              image={undefined}
              source={undefined}
              name={name}
              logo={logo || undefined}
            />
          </SearchHitImageHolder>

          <SearchOrgHitInfo
            hiring={hiring}
            location={location}
            listingStats={listingStats}
            orgStat={orgStat}
            name={name}
            orgType={orgType}
            showLabel={showLabel}
            pillItems={pillItems}
          />
        </SearchHitInteractiveContentWrapper>

        {showSaveButton && (
          <SearchHitSaveButtonHolder>
            <SearchHitSaveButtonContainer hit={hit} />
          </SearchHitSaveButtonHolder>
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
